import type { CSSProperties, ReactNode, RefObject } from "react";
import { valueNames } from "@/modules/Constants";
import {
  isImmersiveLandscapeSize,
  isPhoneScale,
} from "@/modules/orientationManager";
import { findAskConfig, isPixieOpen } from "@/modules/pixieWorkspace";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { PixieWorkspacePhase } from "@/components/player/usePixieWorkspacePhase";
import type { TemplateSizes } from "@/types/layout";
import type { ExperienceState } from "@/types/state";

// Height of the portrait Recipe | Pixie bar above the control bar.
export const PIXIE_BAR_HEIGHT = 58;
// Desktop: the foot of the recipe panel and the workspace the floating switch
// covers (12px inset + 40px track + 2px air). The stylesheet reads it as
// --pixie-switch-clearance, so its offsets follow this one number.
export const PIXIE_SWITCH_CLEARANCE = 54;
export const PIXIE_SWITCH_CLEARANCE_PX = `${PIXIE_SWITCH_CLEARANCE}px`;
// The narrowest the Recipe | Pixie switch reads at: its two labels and the Pixie
// icon. A short window leaves the recipe panel narrower than this, and the switch
// must then grow back over the workspace rather than out past the panel's edge.
const PIXIE_SWITCH_MIN_WIDTH = 172;

export type OnApplicationEvent = (event: Record<string, unknown>) => void;

export type PlayerSizes = TemplateSizes["player"];

// Where the switch starts, from the container's left edge: over the recipe
// panel, but never so far right that it is narrower than it can read.
export function panelSwitchLeft(sizes: PlayerSizes, controlBarWidth: number) {
  const right = sizes.padding.right + controlBarWidth + 12;
  return Math.min(
    sizes.video.width + sizes.video.margins.left + 12,
    sizes.container.width - right - PIXIE_SWITCH_MIN_WIDTH,
  );
}

/** The values both Experience layouts derive from the state. */
export function computeExperienceView(
  experienceState: ExperienceState,
  pixieWorkspacePhase: PixieWorkspacePhase,
) {
  const experienceData = experienceState.experienceData;
  const applicationData = experienceState.applicationData;
  const template = experienceState.template as {
    getSizes(): TemplateSizes;
  };
  const sizes = template.getSizes().player;
  const isPortrait = applicationData.isPortrait();
  // Immersive video-first landscape is phone-scale only. Larger
  // (desktop) landscape keeps the original video + shop-window split — the
  // same JSX this component renders for portrait, whose `!isPortrait` style
  // branches produce the landscape split (geometry from orientationLandscape's
  // split path). Viewport-based so it works under the DevTools device toolbar.
  // The pseudo-fullscreen button (VideoJs) sets window.__pixPseudoFs to
  // force the immersive layout (LandscapeNav tabs + sheet) even in portrait, so
  // "fullscreen" keeps our UI on iOS (which has no element Fullscreen API). The
  // pseudo-fs branch is gated on isPhoneScale() (short side, EITHER orientation) —
  // NOT isImmersiveLandscapeSize() which is false in portrait — so it forces the
  // immersive layout on a portrait phone but never on a large touch device
  // (touchscreen laptop / iPad); the fullscreen button applies the same gate.
  const useImmersive =
    (!isPortrait && isImmersiveLandscapeSize()) ||
    (typeof window !== "undefined" && !!window.__pixPseudoFs && isPhoneScale());
  const shopWindowConfig = applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG,
  ) as ShopWindowConfig | undefined;
  const isLoaded = experienceData.isLoaded();
  let color = "#ffffff";
  // The payload type names `colors`; this reads `color` as the class did.
  const styleColor = experienceData.style?.color as
    | { background?: string }
    | undefined;

  if (experienceData.style && styleColor && styleColor.background) {
    color = styleColor.background;
  }

  const askConfig = isLoaded ? findAskConfig(applicationData) : null;
  const askOpen = !!askConfig && isPixieOpen(applicationData);
  const pixieCovering = askOpen || pixieWorkspacePhase === "leaving";
  const pixieClass = pixieCovering
    ? " experience-container--pixie-open"
    : pixieWorkspacePhase === "closed"
      ? " experience-container--pixie-closed"
      : "";

  const hasControlBar =
    isLoaded && applicationData.getValue(valueNames.HAS_CONTROL_BAR, false);
  const controlBarHeight = hasControlBar ? 45 : 0;
  const controlBarWidth = hasControlBar ? 55 : 0;

  const panelOpen = askOpen || applicationData.showShopWindow();

  return {
    sizes,
    isPortrait,
    useImmersive,
    shopWindowConfig,
    isLoaded,
    color,
    askConfig,
    askOpen,
    pixieClass,
    hasControlBar,
    controlBarHeight,
    controlBarWidth,
    panelOpen,
  };
}

export type ExperienceView = ReturnType<typeof computeExperienceView>;

/** What Experience hands its two layout render functions. */
export type ExperienceLayoutContext = {
  view: ExperienceView;
  experienceState: ExperienceState;
  onApplicationEvent: OnApplicationEvent;
  setContainerEl: (el: HTMLDivElement | null) => void;
  header: ReactNode;
  shopWindow: ReactNode;
  video: ReactNode;
  controlBar: ReactNode;
  refs: Record<
    | "panel"
    | "videoPanel"
    | "favorites"
    | "list"
    | "actionSheet"
    | "landscapeSheet",
    RefObject<HTMLDivElement | null>
  >;
  renderPixieWorkspace(
    askOpen: boolean,
    style: CSSProperties | null,
    compact?: boolean,
  ): ReactNode;
};
